import React, { useEffect, useState } from 'react';
import { Row, Col, Form, Table } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import debounce from 'lodash/debounce';
import { BASE_URL } from '../../../services/Api';
import { authHeader } from '../../../services/auth-header';
import { IoMdClose } from 'react-icons/io';

function CommonSalesQuotationInternationalFields({ onDataChange, initialData }) {
    // Existing customer state
    const [customerOptions, setcustomerOptions] = useState([]);
    const [customerSearch, setcustomerSearch] = useState(" ");
    const [customer, setcustomer] = useState(initialData?.customer || null);
    const [iscustomerOpen, setIscustomerOpen] = useState(false);
    const [customerDetails, setcustomerDetails] = useState({
        contactperson: initialData?.customerDetails?.contactperson || '',
        contactno: initialData?.customerDetails?.contactno || '',
        email: initialData?.customerDetails?.email || '',
        paymentterm: initialData?.customerDetails?.paymentterm || '',
        incoterms: initialData?.customerDetails?.incoterms || ''

    });
    const [customerAddresses, setcustomerAddresses] = useState([]);
    const [customerMultipleAddress, setcustomerMultipleAddress] = useState(initialData?.customerMultipleAddress || null);

    // New state for products and currency
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [rows, setRows] = useState(initialData?.rows || []);
    const [productOptions, setProductOptions] = useState([]);
    const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));
    const [exchangeRate, setExchangeRate] = useState(initialData?.exchangeRate || 0);
    const [exportchargescurrency, setexportchargescurrency] = useState(initialData?.exportchargescurrency || 0);
    const [selectedCurrency, setSelectedCurrency] = useState(initialData?.selectedCurrency || "$ (USD)");
    const [isProductOpen, setIsProductOpen] = useState(false);

    const currencyOptions = [
        { value: "", label: "Select Currency" },
        { value: "$ (USD)", label: "USD" },
        { value: "€ (EUR)", label: "EUR" },
        { value: "¥ (JPY)", label: "JPY" },
        { value: "£ (GBP)", label: "GBP" },
        { value: "₹ (INR)", label: "INR" },
    ];

    const unitOptions = [
        { value: "", label: "Select UOM" },
        { value: "bot", label: "Bot" },
        { value: "ctn", label: "Ctn" },
        { value: "days", label: "Days" },
        { value: "ft", label: "Ft" },
        { value: "hrs", label: "Hrs" },
        { value: "kg", label: "Kg" },
        { value: "ltr", label: "Ltr" },
        { value: "mtr", label: "Mtr" },
        { value: "nos", label: "Nos" },
        { value: "pil", label: "Pil" },
        { value: "pkt", label: "Pkt" },
        { value: "set", label: "Set" },
        { value: "pcs", label: "Pcs" }
    ];

    // Formatting functions
    const formatNumberWithCommas = (num) => {
        if (num === null || num === undefined) return '0.00';
        return parseFloat(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const toWords = (num) => {
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

        function convertLessThanOneThousand(num) {
            if (num === 0) return '';
            if (num < 10) return ones[num];
            if (num < 20) return teens[num - 10];
            if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + ones[num % 10] : '');
            return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 !== 0 ? ' ' + convertLessThanOneThousand(num % 100) : '');
        }

        if (num === 0) return 'Zero';

        // Handle numbers less than 1 lakh (100,000)
        if (num < 100000) {
            const thousandPart = Math.floor(num / 1000);
            const remainder = num % 1000;
            let result = '';
            if (thousandPart > 0) {
                result += convertLessThanOneThousand(thousandPart) + ' Thousand';
            }
            if (remainder > 0) {
                result += (result ? ' ' : '') + convertLessThanOneThousand(remainder);
            }
            return result;
        }

        // Handle numbers 1 lakh and above (Indian numbering system)
        const scales = ['', 'Thousand', 'Lakh', 'Crore', 'Arab', 'Kharab', 'Neel', 'Padma', 'Shankh'];
        let result = '';
        let scaleIndex = 0;
        let remaining = num;

        while (remaining > 0) {
            const chunk = remaining % 100;
            remaining = Math.floor(remaining / 100);

            if (chunk !== 0) {
                let chunkWords = convertLessThanOneThousand(chunk);
                if (scaleIndex > 0) {
                    chunkWords += ' ' + scales[scaleIndex];
                }
                result = chunkWords + ' ' + result;
            }
            scaleIndex++;

            // After lakh, we process numbers in pairs (for lakh, crore etc.)
            if (scaleIndex >= 2) {
                const bigChunk = remaining % 100;
                remaining = Math.floor(remaining / 100);
                if (bigChunk !== 0) {
                    let bigChunkWords = convertLessThanOneThousand(bigChunk);
                    if (scaleIndex > 0) {
                        bigChunkWords += ' ' + scales[scaleIndex];
                    }
                    result = bigChunkWords + ' ' + result;
                }
                scaleIndex++;
            }
        }

        return result.trim();
    };

    const numberToWordsWithRupees = (num) => {
        const amount = Math.round(num);
        return `${toWords(amount)} Rupees Only`;
    };

    // Handle customer details change
    const handleCustomerDetailsChange = (field, value) => {
        setcustomerDetails(prev => ({
            ...prev,
            [field]: value
        }));
    };

    useEffect(() => {
        if (initialData?.customer?.value) {
            getcustomerAddresses(initialData.customer.value);
        }
    }, [initialData]);

    const handleSearchChangecustomer = debounce((inputValue) => {
        setcustomerSearch(inputValue || " ");
    }, 300);

    useEffect(() => {
        getcustomerData();
    }, [customerSearch]);

    const getcustomerData = async () => {
        try {
            const response = await axios.get(
                `${BASE_URL}customer/page/0/10/asc/id/${encodeURIComponent(customerSearch)}`,
                { headers: authHeader() }
            );

            const customers = response.data.Index || response.data.data || [];

            setcustomerOptions(
                customers.map((s) => ({
                    value: s.id,
                    label: s.companyname,
                    contactperson: s.contactperson,
                    contactno: s.contactno,
                    email: s.email,
                    paymentterm: s.paymentterm,
                    incoterms: s.incoterms,
                }))
            );
        } catch (error) {
            console.error("Error fetching customer data", error);
        }
    };

    const getcustomerAddresses = async (id) => {
        try {
            const response = await axios.get(`${BASE_URL}customerMultipleAddress/getAddress/${id}`, {
                headers: authHeader(),
            });
            setcustomerAddresses(response.data.map(addr => ({
                value: addr.id,
                label: addr.customeraddress,
            })));
        } catch (error) {
            console.error("Error fetching customer addresses", error);
        }
    };

    const handlecustomerChange = (selected) => {
        setcustomer(selected);
        if (selected) {
            setcustomerDetails({
                contactperson: selected.contactperson,
                contactno: selected.contactno,
                email: selected.email,
                paymentterm: selected.paymentterm,
                incoterms: selected.incoterms,
            });
            getcustomerAddresses(selected.value);
            setIscustomerOpen(true);
        } else {
            setcustomerDetails({
                contactperson: '',
                contactno: '',
                email: '',
                paymentterm: '',
                incoterms: ''
            });
        }
        setcustomerMultipleAddress(null);
    };

    // New product functions
    const handleProductSearchChange = debounce((inputValue) => {
        setProductSearch(inputValue ? encodeURIComponent(inputValue) : encodeURIComponent(" "));
    }, 500);

    const getProductData = async () => {
        try {
            const response = await axios.get(
                `${BASE_URL}product/page/ptype/0/10/desc/id/2/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
                { headers: authHeader() }
            );

            const products = response.data.Index || response.data.data || [];
            setProductOptions(
                products.map(product => ({
                    value: product.id,
                    label: `${product.description} - ${product.serialnumber} - ${product.partno}`,
                    description: product.description,
                    partno: product.partno || '',
                    serialnumber: product.serialnumber || '',
                    modelnumber: product.modelnumber || '',
                    tagnumber: product.tagnumber || '',
                    moc: product.moc || '',
                    itemcode: product.itemcode || '',
                    hsnsacno: product.hsnsacno || '',
                    uom: product.unitofmeasurement || 'pcs (pcs)',
                    unitrateinr: product.unitrate || 0,
                }))
            );
        } catch (error) {
            console.error("Error fetching products", error);
        }
    };

    useEffect(() => {
        getProductData();
    }, [productSearch]);

    // ✅ FIXED: Added safety checks for product selection
    const handleProductChange = (selectedOption) => {
        if (!selectedOption) {
            setSelectedProduct(null);
            return;
        }
        
        setSelectedProduct(selectedOption);
        const selectedProductData = productOptions.find(product => product.value === selectedOption.value);

        if (selectedProductData) {
            const newRow = {
                description: selectedProductData.description || '',
                value: selectedProductData.value || '',
                productremarks: "",
                quantity: 0,
                unitratecurrency: 0,
                totalcurrency: 0,
                exchangerate: exchangeRate || 1,
                unitRateInr: 0,
                totalInr: 0,
                hsnsacno: selectedProductData.hsnsacno || '',
                itemcode: selectedProductData.itemcode || '',
                serialnumber: selectedProductData.serialnumber || '',
                modelnumber: selectedProductData.modelnumber || '',
                tagnumber: selectedProductData.tagnumber || '',
                moc: selectedProductData.moc || '',
                partno: selectedProductData.partno || '',
                uom: selectedProductData.uom || 'pcs',
                currency: selectedCurrency || "$ (USD)",
            };
            setRows([...rows, newRow]);
            handleDataUpdate();
        }
    };

    // ✅ FIXED: Added boundary checks for row operations
    const handleRowChange = (index, key, value) => {
        if (index < 0 || index >= rows.length) return;
        
        const updatedRows = [...rows];
        updatedRows[index][key] = value;

        if (key === 'quantity' || key === 'unitratecurrency' || key === 'exchangerate') {
            const quantity = parseFloat(updatedRows[index].quantity) || 0;
            const unitratecurrency = parseFloat(updatedRows[index].unitratecurrency) || 0;
            const currentExchangeRate = parseFloat(updatedRows[index].exchangerate) || exchangeRate || 1;

            updatedRows[index].unitRateInr = unitratecurrency * currentExchangeRate;
            updatedRows[index].totalInr = quantity * updatedRows[index].unitRateInr;
            updatedRows[index].totalcurrency = quantity * unitratecurrency;
        }

        setRows(updatedRows);
        handleDataUpdate();
    };

    const removeRow = (index) => {
        if (index < 0 || index >= rows.length) return;
        
        const updatedRows = [...rows];
        updatedRows.splice(index, 1);
        setRows(updatedRows);
        handleDataUpdate();
    };

    const toggleProductSection = () => {
        setIsProductOpen(!isProductOpen);
    };

    const handleDataUpdate = () => {
        if (onDataChange) {
            onDataChange({
                customer,
                customerDetails,
                customerMultipleAddress,
                rows,
                exchangeRate,
                exportchargescurrency,
                exportpreparationchargesinr: (exportchargescurrency || 0) * (exchangeRate || 1),
                selectedCurrency,
                netTotalInr: (rows || []).reduce((total, row) => total + (parseFloat(row.totalInr) || 0), 0),
                netTotalCurrency: (rows || []).reduce((total, row) => total + (parseFloat(row.totalcurrency) || 0), 0),
                grandTotalInr: (rows || []).reduce((total, row) => total + (parseFloat(row.totalInr) || 0), 0) + ((exportchargescurrency || 0) * (exchangeRate || 1)),
                grandTotalCurrency: (rows || []).reduce((total, row) => total + (parseFloat(row.totalcurrency) || 0), 0) + (exportchargescurrency || 0)
            });
        }
    };

    useEffect(() => {
        handleDataUpdate();
    }, [customer, customerDetails, customerMultipleAddress, rows, exchangeRate, exportchargescurrency, selectedCurrency]);

    const togglecustomerSection = () => {
        setIscustomerOpen(!iscustomerOpen);
    };

    // ✅ FIXED: Safe find function with fallback
    const safeFind = (array, predicate, fallback = null) => {
        if (!Array.isArray(array)) return fallback;
        return array.find(predicate) || fallback;
    };

    return (
        <>
            <div className="supplier-accordion-container">
                <div
                    className={`supplier-accordion-header ${iscustomerOpen ? 'supplier-accordion-open' : ''}`}
                    onClick={togglecustomerSection}
                >
                    <h7>customer Information</h7>
                    <span className="supplier-accordion-icon">
                        {iscustomerOpen ? '−' : '+'}
                    </span>
                </div>

                <div className={`supplier-accordion-content ${iscustomerOpen ? 'supplier-accordion-content-show' : ''}`}>
                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>customer</Form.Label>
                                <Select
                                    options={customerOptions}
                                    onChange={handlecustomerChange}
                                    onInputChange={handleSearchChangecustomer}
                                    value={customer}
                                    placeholder="Select customer"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Contact Person</Form.Label>
                                <Form.Control
                                    value={customerDetails.contactperson}
                                    disabled
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Phone</Form.Label>
                                <Form.Control
                                    value={customerDetails.contactno}
                                    disabled
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    value={customerDetails.email}
                                    disabled
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>customer Address</Form.Label>
                                <Select
                                    options={customerAddresses}
                                    value={customerMultipleAddress}
                                    onChange={(val) => setcustomerMultipleAddress(val)}
                                    isClearable
                                    placeholder="Select address"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Payment Term</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={customerDetails.paymentterm}
                                    onChange={e => handleCustomerDetailsChange('paymentterm', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>INCO Term</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={customerDetails.incoterms}
                                    onChange={e => handleCustomerDetailsChange('incoterms', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* New Product and Currency Section */}
            <div className="supplier-accordion-container mt-3">
                <div
                    className={`supplier-accordion-header ${isProductOpen ? 'supplier-accordion-open' : ''}`}
                    onClick={toggleProductSection}
                >
                    <h7>Product & Currency Information</h7>
                    <span className="supplier-accordion-icon">
                        {isProductOpen ? '−' : '+'}
                    </span>
                </div>

                <div className={`supplier-accordion-content ${isProductOpen ? 'supplier-accordion-content-show' : ''}`}>
                    <Row>
                        <Col md="6">
                            <Form.Group controlId="currency">
                                <Form.Label>Currency</Form.Label>
                                {/* ✅ FIXED: Safe find with fallback */}
                                <Select
                                    value={safeFind(currencyOptions, option => option.value === selectedCurrency, currencyOptions[0])}
                                    onChange={(selectedOption) => {
                                        setSelectedCurrency(selectedOption?.value || "$ (USD)");
                                        const updatedRows = rows.map(row => ({
                                            ...row,
                                            currency: selectedOption?.value || "$ (USD)"
                                        }));
                                        setRows(updatedRows);
                                    }}
                                    options={currencyOptions}
                                    placeholder="Select Currency"
                                />
                            </Form.Group>
                        </Col>
                        <Col md="6">
                            <Form.Group controlId="exchangeRate">
                                <Form.Label>Exchange Rate</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={exchangeRate || ''}
                                    onChange={(e) => {
                                        setExchangeRate(parseFloat(e.target.value) || 1);
                                        const updatedRows = rows.map(row => ({
                                            ...row,
                                            exchangerate: parseFloat(e.target.value) || 1,
                                            unitRateInr: (row.unitratecurrency || 0) * (parseFloat(e.target.value) || 1),
                                            totalInr: (row.quantity || 0) * (row.unitratecurrency || 0) * (parseFloat(e.target.value) || 1)
                                        }));
                                        setRows(updatedRows);
                                    }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <hr />
                    <br />

                    <Row>
                        <Col md='12'>
                            <Form.Group>
                                <Form.Label>Select Product</Form.Label>
                                <Select
                                    value={selectedProduct}
                                    onChange={handleProductChange}
                                    options={productOptions}
                                    placeholder="Search and select product"
                                    onInputChange={handleProductSearchChange}
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <br />

                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th>Product Description</th>
                                    <th>Serial No.</th>
                                    <th>Model No.</th>
                                    <th>Tag No.</th>
                                    <th>Part No.</th>
                                    <th>MOC</th>
                                    <th>Item Code</th>
                                    <th>HSN/SAC No.</th>
                                    <th>Quantity</th>
                                    <th>Unit of Measurement</th>
                                    <th>Currency</th>
                                    <th>Unit Rate (Currency)</th>
                                    <th>Total (Currency)</th>
                                    <th>Exchange Rate</th>
                                    <th>Unit Price (INR)</th>
                                    <th>Total Amount (INR)</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, index) => (
                                    <tr key={index}>
                                        <td>
                                            {row.description}
                                            <Form.Control as="textarea" rows={2}
                                                className="remarks"
                                                type="text"
                                                value={row.productremarks || ''}
                                                onChange={(e) => handleRowChange(index, 'productremarks', e.target.value)}
                                                placeholder="Enter item remarks"
                                            />
                                        </td>
                                        <td>{row.serialnumber || '-'}</td>
                                        <td>{row.modelnumber || '-'}</td>
                                        <td>{row.tagnumber || '-'}</td>
                                        <td>{row.partno || '-'}</td>
                                        <td>{row.moc || '-'}</td>
                                        <td>{row.itemcode || '-'}</td>
                                        <td>{row.hsnsacno || '-'}</td>
                                        <td>
                                            <Form.Control
                                                min="0"
                                                className="quantity"
                                                type="number"
                                                value={row.quantity || ''}
                                                onChange={(e) => handleRowChange(index, 'quantity', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            {/* ✅ FIXED: Safe find with fallback */}
                                            <Select
                                                className="uom"
                                                value={safeFind(unitOptions, option => option.value === row.uom, unitOptions[0])}
                                                onChange={(selectedOption) => handleRowChange(index, 'uom', selectedOption?.value || '')}
                                                options={unitOptions}
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                                }}
                                            />
                                        </td>
                                        <td>
                                            {/* ✅ FIXED: Safe find with fallback */}
                                            <Select
                                                className="currency"
                                                value={safeFind(currencyOptions, option => option.value === row.currency, currencyOptions[0])}
                                                options={[safeFind(currencyOptions, option => option.value === row.currency, currencyOptions[0])]}
                                                isDisabled
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                min="0"
                                                className="unitrate"
                                                type="number"
                                                value={row.unitratecurrency || ''}
                                                onChange={(e) => handleRowChange(index, 'unitratecurrency', e.target.value)}
                                            />
                                        </td>
                                        <td>{(parseFloat(row.totalcurrency) || 0).toFixed(2)}</td>
                                        <td>
                                            <Form.Control
                                                min="0"
                                                type="number"
                                                value={row.exchangerate || exchangeRate || 1}
                                                onChange={(e) => handleRowChange(index, 'exchangerate', e.target.value)}
                                                className="exchangerate"
                                            />
                                        </td>
                                        <td>{(parseFloat(row.unitRateInr) || 0).toFixed(2)}</td>
                                        <td>{(parseFloat(row.totalInr) || 0).toFixed(2)}</td>
                                        <td>
                                            <IoMdClose onClick={() => removeRow(index)} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    <div className="card mt-3" style={{ width: '50%', marginLeft: 'auto' }}>
                        <div className="card-header bg-light">
                            <h5 className="mb-0">Quotation Summary</h5>
                        </div>
                        <div className="card-body">
                            <div className="row mb-2">
                                <div className="col-8 text-end">
                                    <strong>Net Total (INR)</strong>
                                </div>
                                <div className="col-4">
                                    <strong>{formatNumberWithCommas(rows.reduce((total, row) => total + (parseFloat(row.totalInr) || 0), 0))}</strong>
                                </div>
                            </div>

                            <div className="row mb-2">
                                <div className="col-8 text-end">
                                    <strong>Net Total (Currency)</strong>
                                </div>
                                <div className="col-4">
                                    <strong>{formatNumberWithCommas(rows.reduce((total, row) => total + (parseFloat(row.totalcurrency) || 0), 0))}</strong>
                                </div>
                            </div>

                            <div className="row mb-2">
                                <div className="col-8 text-end">
                                    <strong>Export Charges (Currency)</strong>
                                </div>
                                <div className="col-4">
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        value={exportchargescurrency || ''}
                                        onChange={(e) => setexportchargescurrency(parseFloat(e.target.value) || 0)}
                                        className="form-control-sm"
                                    />
                                </div>
                            </div>

                            <div className="row mb-2">
                                <div className="col-8 text-end">
                                    <strong>Export Charges (INR)</strong>
                                </div>
                                <div className="col-4">
                                    <strong>{formatNumberWithCommas((exportchargescurrency || 0) * (exchangeRate || 1))}</strong>
                                </div>
                            </div>

                            <div className="row mt-3 pt-2 border-top">
                                <div className="col-8 text-end">
                                    <h5>Grand Total (INR)</h5>
                                </div>
                                <div className="col-4">
                                    <h5>{formatNumberWithCommas(
                                        rows.reduce((total, row) => total + (parseFloat(row.totalInr) || 0), 0) +
                                        ((exportchargescurrency || 0) * (exchangeRate || 1))
                                    )}</h5>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-12 text-end">
                                    <em>{numberToWordsWithRupees(
                                        rows.reduce((total, row) => total + (parseFloat(row.totalInr) || 0), 0) +
                                        ((exportchargescurrency || 0) * (exchangeRate || 1))
                                    )}</em>
                                </div>
                            </div>

                            <div className="row mt-2">
                                <div className="col-8 text-end">
                                    <h5>Grand Total (Currency)</h5>
                                </div>
                                <div className="col-4">
                                    <h5>{formatNumberWithCommas(
                                        rows.reduce((total, row) => total + (parseFloat(row.totalcurrency) || 0), 0) +
                                        (exportchargescurrency || 0)
                                    )}</h5>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default CommonSalesQuotationInternationalFields;